import React, { useEffect, useState } from "react";
import { get } from "lodash";
import { Tabs, TabList, TabPanel, Tab } from "@tvg/design-system";
import {
  RaceProgram,
  RaceTypeCodeEnum,
  RaceWagerType
} from "@tvg/ts-types/Race";
import { ResultPayouts } from "./raceResult/components/runnerResult/types";
import WagerResults from "../../WagerResults";
import RaceResult from "./raceResult";
import useCurrentRaceResultRunners from "../../../hooks/useCurrentRaceResultRunners";

import getWagerTypesDisplay from "./utils/wagerTypesDisplay";
import formatWinningTime from "./utils/formatWinningTime";
import { resultsChangeTabEvent } from "../../../utils/gtm/resultedTabInterections";
import RunnerList from "../../RunnerList";
import RunnerModifier from "../RunnerModifier";
import {
  RunnerModifierEnum,
  RunnerSortByEnum,
  RunnerSortOrderEnum,
  TabSelectionEnum
} from "../../../types";

interface RaceResultViewProps {
  currentRace: RaceProgram;
  numRunnersAllRaces: Record<string, string | undefined>[];
  setTabSelection: React.Dispatch<React.SetStateAction<TabSelectionEnum>>;
  tabSelection: TabSelectionEnum;
  runnerModifier: RunnerModifierEnum;
  runnerSortOrder: RunnerSortOrderEnum;
  runnerSortBy: RunnerSortByEnum;
  selectedBetType: RaceWagerType;
  getRunnerModifier: (val: RunnerModifierEnum) => void;
  selections: string[][];
  onSortOrderChange: (order: RunnerSortOrderEnum) => void;
  onSortByChange: (sortBy: RunnerSortByEnum) => void;
}

const RaceResultView: React.FC<RaceResultViewProps> = ({
  currentRace,
  numRunnersAllRaces,
  setTabSelection,
  tabSelection,
  runnerModifier,
  runnerSortOrder,
  runnerSortBy,
  selectedBetType,
  getRunnerModifier,
  selections,
  onSortOrderChange,
  onSortByChange
}) => {
  const [totalPayouts, setTotalPayouts] = useState<ResultPayouts>({
    win: undefined,
    place: undefined,
    show: undefined
  });

  const isDriverRace = currentRace?.type?.code === RaceTypeCodeEnum.HARNESS;
  const bettingInterests = get(currentRace, "bettingInterests", []);
  const results = get(currentRace, "results");
  const winningTime = get(currentRace, "results.winningTime");
  const payoffs = get(results, "payoffs", []);

  const { runnersBI, runnersScratched } = useCurrentRaceResultRunners(
    bettingInterests,
    results,
    currentRace.id,
    isDriverRace
  );

  const dispatchGtmEvent = (selection: string) => {
    const label = selection.charAt(0).toUpperCase() + selection.slice(1);
    resultsChangeTabEvent({
      textLabel: label,
      raceNumber: currentRace?.raceNumber!,
      trackName: currentRace?.track?.trackName
    });
  };
  const saddleType = get(currentRace, "type.name", "thoroughbred");
  const raceNumber = get(currentRace, "raceNumber", 0);

  useEffect(() => {
    if (results?.runners) {
      setTotalPayouts(getWagerTypesDisplay(results?.runners));
    }
  }, [results]);

  const isSH5Available =
    payoffs && payoffs.some((item) => item.wagerType.code === "SH5");

  const showMaxResults = Math.min(isSH5Available ? 5 : 4, runnersBI.length);

  return (
    <Tabs
      selectedValue={tabSelection}
      onChange={(val) => {
        setTabSelection(val as TabSelectionEnum);
        dispatchGtmEvent(val as TabSelectionEnum);
      }}
      qaLabel="tab"
    >
      <TabList qaLabel="tabList" isStretched>
        <Tab value={TabSelectionEnum.RESULT} title="Result" />
        <Tab value={TabSelectionEnum.RACECARD} title="Racecard" />
      </TabList>
      <TabPanel value={TabSelectionEnum.RESULT}>
        <>
          <RaceResult
            totalPayouts={totalPayouts}
            runnerResultsList={runnersBI}
            scratchedRunnersSelection={runnersScratched}
            winningTime={formatWinningTime(winningTime)}
            showMaxResults={showMaxResults}
          />
          <WagerResults
            results={results}
            saddleType={saddleType.toLowerCase()}
            raceNumber={+raceNumber}
          />
        </>
      </TabPanel>
      <TabPanel value={TabSelectionEnum.RACECARD}>
        <div>
          <RunnerModifier
            value={runnerModifier}
            getRunnerModifier={getRunnerModifier}
            betType={selectedBetType}
            selections={selections}
            sortOrder={runnerSortOrder}
            sortBy={runnerSortBy}
            onSortOrderChange={onSortOrderChange}
            onSortByChange={onSortByChange}
            currentRace={currentRace}
          />
          <RunnerList
            key={currentRace.id || ""}
            currentRace={currentRace}
            runnerModifier={runnerModifier}
            runnerSortOrder={runnerSortOrder}
            runnerSortBy={runnerSortBy}
            numRunnersAllRaces={numRunnersAllRaces}
          />
        </div>
      </TabPanel>
    </Tabs>
  );
};

export default RaceResultView;
